// src/pages/PartnerEmpresaHome.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Avatar,
  Divider,
  Chip,
  Stack,
  Button,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";


import { doc, getDoc } from "firebase/firestore";
import PeopleIcon from "@mui/icons-material/People";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";

import { useAuth } from "../contexts/AuthContext";
import { db } from "../contexts/firebase/config";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";
import { Link as RouterLink } from "react-router-dom";

import sintanetLogo from "../assets/sintanet-logo.png";

function formatMoney(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function formatDate(value) {
  try {
    const d = value?.toDate?.() || (value ? new Date(value) : null);
    return d && !isNaN(d) ? d.toLocaleDateString("pt-PT") : "—";
  } catch {
    return "—";
  }
}

export default function PartnerEmpresaHome() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    accent: "#ff6a00",
    success: "#ff8a1f",
    warning: theme.palette.warning.main,
    card: isDark ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.94)",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: "#080808",
  };

  const { currentUser } = useAuth();
  const empresaId = currentUser?.uid;

  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
  if (!empresaId) return;

  const loadCompany = async () => {
    const ref = doc(db, "empresas", empresaId);
    const snap = await getDoc(ref);

    if (snap.exists()) {
      setCompanyName(snap.data().nomeEmpresa); // ou snap.data().nome
    }
  };

  loadCompany();
}, [empresaId]);

  const [stats, setStats] = useState({
    totalClients: 0,
    totalComissao: 0,
    pendingComissao: 0,
    paidComissao: 0,
  });

  const [recentClients, setRecentClients] = useState([]);
  const [recentComs, setRecentComs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!empresaId) return;

    const unsubAllCli = onSnapshot(
      query(collection(db, "clientes"), where("empresaId", "==", empresaId)),
      (snap) => setStats((s) => ({ ...s, totalClients: snap.size }))
    );

    const unsubCli5 = onSnapshot(
      query(
        collection(db, "clientes"),
        where("empresaId", "==", empresaId),
        orderBy("dataRegisto", "desc"),
        limit(5)
      ),
      (snap) =>
        setRecentClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    const unsubComAll = onSnapshot(
      query(collection(db, "compras"), where("empresaId", "==", empresaId)),
      (snap) => {
        let total = 0;
        let pend = 0;
        let paid = 0;

        snap.docs.forEach((d) => {
          const { comissao = 0, status = "" } = d.data();
          total += Number(comissao || 0);

          if (status === "Pago") paid += Number(comissao || 0);
          else pend += Number(comissao || 0);
        });

        setStats((s) => ({
          ...s,
          totalComissao: total,
          pendingComissao: pend,
          paidComissao: paid,
        }));

        setLoading(false);
      },
      () => setLoading(false)
    );

    const unsubCom5 = onSnapshot(
      query(
        collection(db, "compras"),
        where("empresaId", "==", empresaId),
        orderBy("dataCompra", "desc"),
        limit(5)
      ),
      (snap) =>
        setRecentComs(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );

    return () => {
      unsubAllCli();
      unsubCli5();
      unsubComAll();
      unsubCom5();
    };
  }, [empresaId]);

  const partnerName = companyName || "Parceiro";

  const paidPercentage = useMemo(() => {
    if (!stats.totalComissao) return 0;
    return Math.round((stats.paidComissao / stats.totalComissao) * 100);
  }, [stats.totalComissao, stats.paidComissao]);

  const cards = [
    {
      label: "Clientes",
      value: stats.totalClients,
      icon: <PeopleIcon />,
      to: "/dashboard/empresa/clientes",
      hint: "Clientes associados",
      color: colors.accent,
    },
    {
      label: "Comissão total",
      value: formatMoney(stats.totalComissao),
      icon: <MonetizationOnIcon />,
      to: "/dashboard/empresa/comissoes",
      hint: "Valor acumulado",
      color: colors.success,
    },
    {
      label: "Pendente",
      value: formatMoney(stats.pendingComissao),
      icon: <HourglassEmptyIcon />,
      hint: "Ainda por liquidar",
      color: colors.warning,
    },
    {
      label: "Pago",
      value: formatMoney(stats.paidComissao),
      icon: <CheckCircleIcon />,
      hint: "Comissões liquidadas",
      color: colors.success,
    },
  ];

  const StatCard = ({ label, value, icon, to, hint, color }) => {
    const content = (
      <Card
        elevation={0}
        sx={{
          height: "100%",
          borderRadius: "26px",
          border: `1px solid ${colors.border}`,
          bgcolor: colors.card,
          overflow: "hidden",
          position: "relative",
          boxShadow: isDark
            ? "0 20px 55px rgba(0,0,0,0.28)"
            : "0 18px 45px rgba(15,23,42,0.07)",
          transition: "0.22s ease",
          "&:hover": {
            transform: to ? "translateY(-4px)" : "translateY(-2px)",
            borderColor: alpha(color, 0.35),
          },
          "&:before": {
            content: '""',
            position: "absolute",
            width: 180,
            height: 180,
            right: -80,
            bottom: -90,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${alpha(
              color,
              isDark ? 0.18 : 0.12
            )} 0%, transparent 70%)`,
          },
        }}
      >
        <CardContent sx={{ p: 2.7, position: "relative", zIndex: 1 }}>
          <Stack spacing={2.3}>
            <Stack direction="row" justifyContent="space-between">
              <Avatar
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: "19px",
                  bgcolor: alpha(color, isDark ? 0.18 : 0.1),
                  color,
                }}
              >
                {icon}
              </Avatar>

              {to && (
                <Avatar
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "14px",
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(248,250,252,0.9)",
                    color: colors.muted,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <ArrowForwardRoundedIcon fontSize="small" />
                </Avatar>
              )}
            </Stack>

            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 950,
                  color: colors.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                }}
              >
                {label}
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  fontSize: { xs: "2rem", md: "2.3rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.07em",
                  lineHeight: 1,
                  color: colors.text,
                }}
              >
                {value}
              </Typography>

              <Typography sx={{ mt: 0.8, color: colors.muted, fontSize: 14 }}>
                {hint}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );

    if (to) {
      return (
        <CardActionArea
          component={RouterLink}
          to={to}
          sx={{ height: "100%", borderRadius: "26px" }}
        >
          {content}
        </CardActionArea>
      );
    }

    return content;
  };

  const SectionCard = ({ title, subtitle, icon, count, actionTo, children }) => (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: "28px",
        border: `1px solid ${colors.border}`,
        bgcolor: colors.card,
        boxShadow: isDark
          ? "0 22px 60px rgba(0,0,0,0.26)"
          : "0 18px 45px rgba(15,23,42,0.06)",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ px: { xs: 2.2, md: 3 }, py: 2.4 }}
      >
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            sx={{
              width: 46,
              height: 46,
              borderRadius: "17px",
              bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
              color: colors.accent,
            }}
          >
            {icon}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, fontSize: "1.1rem" }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={count}
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
              color: colors.accent,
            }}
          />

          {actionTo && (
            <Button
              component={RouterLink}
              to={actionTo}
              size="small"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                color: colors.accent,
              }}
            >
              Ver tudo
            </Button>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ borderColor: colors.border }} />
      {children}
    </Paper>
  );

  const tableHeadSx = {
    "& th": {
      bgcolor: isDark ? "rgba(2,6,23,0.38)" : "rgba(248,250,252,0.92)",
      fontWeight: 900,
      color: colors.muted,
      borderBottom: `1px solid ${colors.border}`,
      fontSize: "0.76rem",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      py: 1.35,
    },
  };

  const tableRowSx = {
    "& td": {
      borderBottom: `1px solid ${colors.border}`,
      py: 1.7,
    },
    "&:last-child td": { borderBottom: 0 },
    "&:hover": {
      bgcolor: alpha(colors.accent, isDark ? 0.08 : 0.045),
    },
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 76px)",
        width: "100%",
        position: "relative",
        px: { xs: 1.5, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 55%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #fff7ed 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: isDark ? 0.16 : 0.28,
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 72%)",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "1520px",
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.4, md: 3.6 },
            borderRadius: "32px",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.14)",
            overflow: "hidden",
            position: "relative",
            boxShadow: isDark
              ? "0 26px 80px rgba(0,0,0,0.34)"
              : "0 24px 70px rgba(15,23,42,0.2)",
            background:
              "radial-gradient(circle at 82% 28%, rgba(255,106,0,0.42) 0%, rgba(255,106,0,0.18) 24%, transparent 48%), linear-gradient(135deg, #05070b 0%, #121212 52%, #ff6a00 180%)",
            "&:before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${sintanetLogo})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: {
                xs: "center 22px",
                lg: "right 60px center",
              },
              backgroundSize: {
                xs: "260px auto",
                md: "420px auto",
                lg: "520px auto",
              },
              opacity: 0.12,
              filter: "drop-shadow(0 24px 50px rgba(255,106,0,0.35))",
              pointerEvents: "none",
            },
          }}
        >
          <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
            <Grid item xs={12} lg={8}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <Avatar
                  src={sintanetLogo}
                  variant="rounded"
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: "18px",
                    bgcolor: "rgba(255,106,0,0.16)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    p: 0.8,
                    "& img": { objectFit: "contain" },
                  }}
                />

                <Typography
                  variant="overline"
                  sx={{
                    fontSize: 12,
                    fontWeight: 900,
                    letterSpacing: "0.18em",
                    color: "rgba(255,255,255,0.72)",
                  }}
                >
                  Painel do Parceiro
                </Typography>
              </Stack>

              <Typography
                sx={{
                  fontSize: { xs: "2.15rem", sm: "3rem", md: "4rem" },
                  fontWeight: 950,
                  lineHeight: 0.95,
                  letterSpacing: "-0.08em",
                  color: "#fff",
                }}
              >
                Olá, {partnerName}
              </Typography>

              <Typography
                sx={{
                  mt: 1.7,
                  maxWidth: 820,
                  color: "rgba(255,255,255,0.72)",
                  fontSize: { xs: "0.98rem", md: "1.08rem" },
                  lineHeight: 1.75,
                }}
              >
                Acompanha clientes, comissões e pagamentos num painel moderno com a
                identidade visual da Sintanet.
              </Typography>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.4 }}>
                <Chip
                  icon={<WorkspacePremiumRoundedIcon />}
                  label="Conta Parceiro"
                  sx={{
                    height: 36,
                    borderRadius: 999,
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.16)",
                    "& .MuiChip-icon": { color: "#ff8a1f" },
                  }}
                />

                <Chip
                  label={`${stats.totalClients} clientes`}
                  sx={{
                    height: 36,
                    borderRadius: 999,
                    fontWeight: 900,
                    color: "#fff",
                    bgcolor: "rgba(255,106,0,0.26)",
                    border: "1px solid rgba(255,255,255,0.16)",
                  }}
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 3 }}>
                <Button
                  component={RouterLink}
                  to="/dashboard/empresa/clientes"
                  variant="contained"
                  startIcon={<PeopleIcon />}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 950,
                    px: 2.4,
                    bgcolor: "#ff6a00",
                    color: "#fff",
                    boxShadow: "0 14px 35px rgba(255,106,0,0.35)",
                    "&:hover": { bgcolor: "#ff7a1a" },
                  }}
                >
                  Ver Clientes
                </Button>

                <Button
                  component={RouterLink}
                  to="/dashboard/empresa/comissoes"
                  variant="outlined"
                  startIcon={<ReceiptLongRoundedIcon />}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 950,
                    px: 2.4,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.28)",
                    "&:hover": {
                      borderColor: "#ff6a00",
                      bgcolor: "rgba(255,106,0,0.12)",
                    },
                  }}
                >
                  Ver Comissões
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.2,
                  borderRadius: "26px",
                  bgcolor: "rgba(10,10,10,0.48)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  backdropFilter: "blur(18px)",
                }}
              >
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1.3} alignItems="center">
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "18px",
                        bgcolor: "rgba(255,106,0,0.22)",
                        color: "#fff",
                      }}
                    >
                      <AutoGraphRoundedIcon />
                    </Avatar>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: "rgba(255,255,255,0.66)",
                          fontWeight: 800,
                        }}
                      >
                        Progresso financeiro
                      </Typography>

                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 950 }}>
                        {paidPercentage}% liquidado
                      </Typography>
                    </Box>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={paidPercentage}
                    sx={{
                      height: 10,
                      borderRadius: 999,
                      bgcolor: "rgba(255,255,255,0.16)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        bgcolor: "#ff6a00",
                      },
                    }}
                  />

                  <Divider sx={{ borderColor: "rgba(255,255,255,0.14)" }} />

                  {[
                    ["Total", stats.totalComissao],
                    ["Pago", stats.paidComissao],
                    ["Pendente", stats.pendingComissao],
                  ].map(([label, value]) => (
                    <Stack key={label} direction="row" justifyContent="space-between">
                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.64)",
                          fontWeight: 800,
                        }}
                      >
                        {label}
                      </Typography>

                      <Typography sx={{ color: "#fff", fontWeight: 950 }}>
                        {formatMoney(value)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2.4} sx={{ mb: 3 }}>
              {cards.map((c) => (
                <Grid key={c.label} item xs={12} sm={6} lg={3}>
                  <StatCard {...c} />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2.4}>
              <Grid item xs={12} lg={6}>
                <SectionCard
                  title="Clientes recentes"
                  subtitle="Últimos clientes associados à tua empresa."
                  icon={<PersonAddAltRoundedIcon />}
                  count={recentClients.length}
                  actionTo="/dashboard/empresa/clientes"
                >
                  <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "transparent" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={tableHeadSx}>
                          <TableCell>Nome</TableCell>
                          <TableCell>Data</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {recentClients.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={2}
                              align="center"
                              sx={{ py: 5, color: "text.secondary", borderBottom: 0 }}
                            >
                              Nenhum cliente recente.
                            </TableCell>
                          </TableRow>
                        ) : (
                          recentClients.map((c) => (
                            <TableRow key={c.id} hover sx={tableRowSx}>
                              <TableCell>
                                <Stack direction="row" spacing={1.2} alignItems="center">
                                  <Avatar
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      fontSize: 14,
                                      fontWeight: 900,
                                      bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                                      color: colors.accent,
                                    }}
                                  >
                                    {c.nome?.charAt(0)?.toUpperCase() || "C"}
                                  </Avatar>

                                  <Typography sx={{ fontWeight: 850, color: colors.text }}>
                                    {c.nome || "Sem nome"}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              <TableCell sx={{ color: colors.muted }}>
                                {formatDate(c.dataRegisto)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SectionCard>
              </Grid>

              <Grid item xs={12} lg={6}>
                <SectionCard
                  title="Comissões recentes"
                  subtitle="Últimos registos de comissões da empresa."
                  icon={<ReceiptLongRoundedIcon />}
                  count={recentComs.length}
                  actionTo="/dashboard/empresa/comissoes"
                >
                  <TableContainer component={Paper} elevation={0} sx={{ bgcolor: "transparent" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={tableHeadSx}>
                          <TableCell>Cliente</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell align="right">Comissão</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {recentComs.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={3}
                              align="center"
                              sx={{ py: 5, color: "text.secondary", borderBottom: 0 }}
                            >
                              Nenhuma comissão recente.
                            </TableCell>
                          </TableRow>
                        ) : (
                          recentComs.map((c) => (
                            <TableRow key={c.id} hover sx={tableRowSx}>
                              <TableCell>
                                <Typography sx={{ fontWeight: 850, color: colors.text }}>
                                  {c.clienteNome || "—"}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ color: colors.muted }}>
                                {formatDate(c.dataCompra)}
                              </TableCell>

                              <TableCell align="right">
                                <Chip
                                  size="small"
                                  icon={<AccountBalanceWalletRoundedIcon />}
                                  label={
                                    typeof c.comissao === "number"
                                      ? formatMoney(c.comissao)
                                      : "—"
                                  }
                                  sx={{
                                    borderRadius: 999,
                                    fontWeight: 900,
                                    bgcolor: alpha(colors.success, isDark ? 0.16 : 0.08),
                                    color: colors.success,
                                    "& .MuiChip-icon": { color: colors.success },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </SectionCard>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
}